<template>
  <UiModal
    :is-shown="open"
    :teleport-target="teleportTarget || undefined"
    modal-class="modal-city-select"
    size="s"
    is-header-fixed
    title="Выберите город"
    :is-footer-hidden="true"
    :is-close-button-filled="true"
    @close="emit('close')"
  >
    <template #content>
      <div class="city-select">
        <UiInput
          :value="query"
          class="city-select__input"
          placeholder="Поиск города"
          is-full-width
          is-clearable
          @input-value="onInput"
          @clear="onInput('')"
        >
          <template #prefix>
            <svg
              class="city-select__search-icon"
              xmlns="http://www.w3.org/2000/svg"
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
            >
              <path
                d="M10.9988 9.67336H10.3037L10.0574 9.43594C11.1132 8.20485 11.6587 6.5253 11.3596 4.74023C10.946 2.29564 8.90475 0.343495 6.44114 0.0445174C2.71932 -0.412743 -0.412985 2.71773 0.0445435 6.43736C0.343697 8.89953 2.29699 10.9396 4.74301 11.3529C6.52913 11.6519 8.20966 11.1067 9.44147 10.0515L9.67903 10.2977V10.9924L13.4184 14.7296C13.7792 15.0901 14.3687 15.0901 14.7294 14.7296C15.0902 14.3691 15.0902 13.7799 14.7294 13.4194L10.9988 9.67336ZM5.71965 9.67336C3.5288 9.67336 1.76027 7.90587 1.76027 5.7163C1.76027 3.52673 3.5288 1.75924 5.71965 1.75924C7.91051 1.75924 9.67903 3.52673 9.67903 5.7163C9.67903 7.90587 7.91051 9.67336 5.71965 9.67336Z"
                fill="currentColor"
              />
            </svg>
          </template>
        </UiInput>

        <ul class="city-select__list">
          <li
            v-for="city in cities"
            :key="city.cityPk"
            class="city-select__item"
            role="button"
            tabindex="0"
            @click="onSelect(city)"
            @keydown.enter="onSelect(city)"
            @keydown.space.prevent="onSelect(city)"
          >
            <span class="city-select__pin">
              <svg
                class="city-select__pin-icon"
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="20"
                viewBox="0 0 16 20"
                fill="none"
              >
                <path
                  d="M8 17.35C10.0333 15.4833 11.5417 13.7875 12.525 12.2625C13.5083 10.7375 14 9.38333 14 8.2C14 6.38333 13.4208 4.89583 12.2625 3.7375C11.1042 2.57917 9.68333 2 8 2C6.31667 2 4.89583 2.57917 3.7375 3.7375C2.57917 4.89583 2 6.38333 2 8.2C2 9.38333 2.49167 10.7375 3.475 12.2625C4.45833 13.7875 5.96667 15.4833 8 17.35ZM8 19.325C7.76667 19.325 7.53333 19.2833 7.3 19.2C7.06667 19.1167 6.85833 18.9917 6.675 18.825C5.59167 17.825 4.63333 16.85 3.8 15.9C2.96667 14.95 2.27083 14.0292 1.7125 13.1375C1.15417 12.2458 0.729167 11.3875 0.4375 10.5625C0.145833 9.7375 0 8.95 0 8.2C0 5.7 0.804167 3.70833 2.4125 2.225C4.02083 0.741667 5.88333 0 8 0C10.1167 0 11.9792 0.741667 13.5875 2.225C15.1958 3.70833 16 5.7 16 8.2C16 8.95 15.8542 9.7375 15.5625 10.5625C15.2708 11.3875 14.8458 12.2458 14.2875 13.1375C13.7292 14.0292 13.0333 14.95 12.2 15.9C11.3667 16.85 10.4083 17.825 9.325 18.825C9.14167 18.9917 8.93333 19.1167 8.7 19.2C8.46667 19.2833 8.23333 19.325 8 19.325ZM8 10C8.55 10 9.02083 9.80417 9.4125 9.4125C9.80417 9.02083 10 8.55 10 8C10 7.45 9.80417 6.97917 9.4125 6.5875C9.02083 6.19583 8.55 6 8 6C7.45 6 6.97917 6.19583 6.5875 6.5875C6.19583 6.97917 6 7.45 6 8C6 8.55 6.19583 9.02083 6.5875 9.4125C6.97917 9.80417 7.45 10 8 10Z"
                  fill="currentColor"
                />
              </svg>
            </span>
            <span class="city-select__text">
              <span class="city-select__name">{{ city.cityNameOnly }}</span>
              <span class="city-select__country">{{ city.countryName }}</span>
            </span>
          </li>

          <li
            v-if="isLoading"
            class="city-select__state"
          >
            Загрузка…
          </li>

          <li
            v-else-if="isQueryTooShort"
            class="city-select__state"
          >
            Начните вводить название города
          </li>

          <li
            v-else-if="!cities.length"
            class="city-select__state"
          >
            Ничего не найдено
          </li>
        </ul>
      </div>
    </template>
  </UiModal>
</template>

<script setup lang="ts">
  import { UiInput } from '@experience/core-input-ui';
  import { UiModal } from '@experience/core-modal-ui';
  import { ref, computed, watch } from 'vue';

  import { getGeoTypeahead, type CitySuggest } from '@/shared/api/generated/api';
  import { trackEvent } from '@/shared/lib/analytics/compat';

  const MIN_QUERY_LENGTH = 2;
  const DEBOUNCE_MS = 300;

  const props = withDefaults(
    defineProps<{
      open: boolean;
      teleportTarget?: HTMLElement | null;
      analyticsBase?: Record<string, unknown>;
    }>(),
    {
      teleportTarget: null,
      analyticsBase: () => ({}),
    },
  );

  const emit = defineEmits<{
    close: [];
    select: [city: CitySuggest];
  }>();

  const query = ref('');
  const cities = ref<CitySuggest[]>([]);
  const isLoading = ref(false);

  const onInput = (value: string | number) => {
    query.value = String(value);
  };

  const normalizedQuery = computed(() => query.value.trim());
  const isQueryTooShort = computed(() => normalizedQuery.value.length < MIN_QUERY_LENGTH);

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let requestId = 0;

  const fetchCities = async (search: string) => {
    const currentRequestId = ++requestId;

    isLoading.value = true;

    try {
      const { data } = await getGeoTypeahead({
        query: {
          query: search,
          is_city_suggest: true,
          handle_city_duplicates: true,
        },
      });

      if (currentRequestId !== requestId) return;

      cities.value = data ?? [];
    } catch {
      if (currentRequestId !== requestId) return;

      cities.value = [];
    } finally {
      if (currentRequestId === requestId) {
        isLoading.value = false;
      }
    }
  };

  const resetResults = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    requestId += 1;
    cities.value = [];
    isLoading.value = false;
  };

  watch(normalizedQuery, (search) => {
    if (debounceTimer) clearTimeout(debounceTimer);

    if (search.length < MIN_QUERY_LENGTH) {
      resetResults();

      return;
    }

    debounceTimer = setTimeout(() => void fetchCities(search), DEBOUNCE_MS);
  });

  const onSelect = async (city: CitySuggest) => {
    await trackEvent('around:hero-page:city-search:city.click', {
      ...props.analyticsBase,
      target_search_id: city.cityPk,
      target_search_name: city.cityNameOnly,
    });

    emit('select', city);
    emit('close');
  };

  watch(
    () => props.open,
    (isOpen) => {
      if (isOpen) {
        void trackEvent('around:hero-page:city-search.view', { ...props.analyticsBase });

        return;
      }

      query.value = '';
      resetResults();
    },
  );
</script>

<style scoped lang="scss">
  .city-select {
    display: flex;
    flex-direction: column;

    &__search-icon {
      flex-shrink: 0;
      color: var(--alias-color-icon-default);
    }

    &__list {
      display: flex;
      flex-direction: column;
      max-height: 320px;
      padding: 0;
      margin: 0;
      overflow-y: auto;
      list-style: none;
    }

    &__item {
      display: flex;
      gap: 12px;
      align-items: center;
      padding: 8px 0;
      cursor: pointer;
      border-radius: 12px;
      transition: background 0.15s ease;

      &:hover,
      &:focus-visible {
        outline: none;
        background: var(--alias-color-background-subtle);
      }
    }

    &__pin {
      display: flex;
      flex-shrink: 0;
      align-items: center;
      justify-content: center;
      width: 48px;
      height: 48px;
      background: var(--alias-color-background-subtle);
      border-radius: 8px;
    }

    &__pin-icon {
      color: var(--alias-color-icon-default);
    }

    &__text {
      display: flex;
      flex-direction: column;
    }

    &__name {
      color: var(--alias-color-text-default);

      @include base-400-300-regular;
    }

    &__country {
      color: var(--alias-color-text-subtlest);

      @include base-300-300-regular;
    }

    &__state {
      padding: 16px 8px;
      font-size: 14px;
      color: var(--alias-color-text-subtlest);
      list-style: none;
    }
  }

  .city-select__input {
    margin-bottom: 8px;
  }
</style>
